import React, { useEffect, useCallback, useRef, useState } from 'react'
import { Form, Alert, Row, Col } from 'react-bootstrap'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getCustomerInfoCookieName, getErrorWithFullMessage } from 'exports'
import customerDataApiService from '../../services/customerDataApiService'
import { ICustomer } from '../../interfaces/i-api-loan'
import { CookieType } from '../../constants/cookieTypes'
import Input from '../input/Input'
import appConfig from 'app.config'
import ReactPhoneInput from 'react-phone-input-2'
import { AddressService } from 'components/addressService/AddressService'
import { IAddressService } from '../../interfaces/i-api-loan'
import { AddressFormData } from './addressSchema'
import { TranslationFile } from 'i18n'
interface IChangeAddress {
	handleClose: (customerData: ICustomer) => void
	user: ICustomer
	hide: () => void
}

const isEstonianMarket = appConfig.country === 'ee'
const allowMultipleFormats = ['fi', 'ee', 'lt'].includes(appConfig.country)

function ChangeAddress({ hide, handleClose, user }: IChangeAddress) {
	const [error, setError] = React.useState<Error>()
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
	])
	const {
		handleSubmit,
		reset,
		setValue,
		control,
		formState: { errors, isSubmitting },
	} = useForm<AddressFormData>({
		defaultValues: {
			address: {
				street: user.address?.street || '',
				zip: user.address?.zip || '',
				city: user.address?.city || '',
				street2: user.address?.street2 || '',
				customerCountry: user.address?.customerCountry || '',
			},
			contacts: {
				email: user.contacts?.email || '',
				cellPhone: user.contacts?.cellPhone || '',
			},
		},
	})
	const changeAddressComponentRef = useRef<HTMLDivElement>(null)
	const [isDepositCustomer, setIsDepositCustomer] = useState<boolean>()

	useEffect(() => {
		setIsDepositCustomer(
			(sessionStorage.getItem(CookieType.DepositCustomer) ?? '') ===
				'true',
		)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sessionStorage])

	useEffect(() => {
		reset({
			address: {
				street: user.address?.street || '',
				zip: user.address?.zip || '',
				city: user.address?.city || '',
				street2: user.address?.street2 || '',
				customerCountry: user.address?.customerCountry || '',
			},
			contacts: {
				email: user.contacts?.email || '',
				cellPhone: user.contacts?.cellPhone || '',
			},
		})
	}, [user, reset])
	const onSubmit: SubmitHandler<AddressFormData> = async (data) => {
		const updatedValues: ICustomer = {
			...user,
			address: {
				...user.address,
				...data.address,
			},
			contacts: {
				...user.contacts,
				...data.contacts,
			},
		}

		const storedValue = sessionStorage.getItem(getCustomerInfoCookieName())
		if (storedValue && JSON.stringify(updatedValues) !== storedValue) {
			try {
				await customerDataApiService.updateCustomerDetails(
					updatedValues,
					t,
				)
				handleClose(updatedValues)
			} catch (err: any) {
				setError(getErrorWithFullMessage(err))
				console.error(err)
			}
		} else {
			handleClose(updatedValues)
		}
	}

	const handleAddressSelect = useCallback(
		(selectedAddress: IAddressService) => {
			setValue('address.street', selectedAddress.street || '')
			setValue('address.zip', selectedAddress.postalCode || '')
			setValue('address.city', selectedAddress.city || '')
		},
		[setValue],
	)

	useEffect(() => {
		const changeAddressComponent = changeAddressComponentRef.current
		if (!changeAddressComponent) return

		const observer = new MutationObserver(() => {
			const selectedFlagDiv =
				changeAddressComponent.querySelector('.selected-flag')
			const ulCountryList =
				changeAddressComponent.querySelector('.country-list')
			if (
				selectedFlagDiv &&
				!selectedFlagDiv.hasAttribute('aria-label')
			) {
				selectedFlagDiv.setAttribute(
					'aria-label',
					t('WCAG-Label_SelectedCountryFlag'),
				)
			}
			if (ulCountryList && !ulCountryList.hasAttribute('aria-label')) {
				ulCountryList.setAttribute(
					'aria-label',
					t('forms:WCAG-Label_DropDownCountry'),
				)
			}
			if (
				selectedFlagDiv?.hasAttribute('aria-label') &&
				ulCountryList?.hasAttribute('aria-label')
			) {
				observer.disconnect()
			}
		})
		observer.observe(changeAddressComponent, {
			childList: true,
			subtree: true,
		})

		return () => observer.disconnect()
	}, [t])

	return (
		<div id="change-address-component" ref={changeAddressComponentRef}>
			<div className="content">
				<div className="title">{t('ChangeContact')}</div>
				{error && (
					<Alert
						variant="danger"
						dismissible
						onClose={() => setError(undefined)}>
						{error.message}
					</Alert>
				)}
				<Form onSubmit={handleSubmit(onSubmit)} noValidate>
					{isDepositCustomer && (
						<Row>
							<Col xs={12}>
								<Input
									id="fullname-input"
									label={t('FullName')}
									disabled={true}
									name={'personalInfo.fullname'}
									value={`${
										user.personalInfo?.firstName || ''
									} ${user.personalInfo?.lastName || ''}`}
									type={'text'}
								/>
							</Col>
							<Col xs={12}>
								<Input
									id="firstname-input"
									label={t('Firstname')}
									disabled={true}
									name={'personalInfo.firstName'}
									value={user.personalInfo?.firstName || ''}
									type="text"
								/>
							</Col>
							<Col xs={12}>
								<Input
									id="lastname-input"
									label={t('Lastname')}
									disabled={true}
									name={'personalInfo.lastName'}
									value={user.personalInfo?.lastName || ''}
									type="text"
								/>
							</Col>
						</Row>
					)}
					{isEstonianMarket ? (
						<AddressService
							onSelectAddress={handleAddressSelect}
							user={user}
						/>
					) : (
						<Row>
							<Col xs={12}>
								<Controller
									control={control}
									name="address.street"
									render={({ field }) => (
										<Input
											{...field}
											id="addressline-input"
											label={t('Addressline')}
											disabled={true}
											type="text"
											error={
												errors.address?.street?.message
											}
										/>
									)}
								/>
							</Col>
							<Col xs={5}>
								<Controller
									control={control}
									name="address.zip"
									render={({ field }) => (
										<Input
											{...field}
											id="zip-input"
											label={t('Zip')}
											type="text"
											disabled={true}
											error={errors.address?.zip?.message}
										/>
									)}
								/>
							</Col>
							<Col xs={7}>
								<Controller
									control={control}
									name="address.city"
									render={({ field }) => (
										<Input
											{...field}
											id="city-input"
											label={t('City')}
											type="text"
											disabled={true}
											error={
												errors.address?.city?.message
											}
										/>
									)}
								/>
							</Col>
						</Row>
					)}
					<Row>
						<Col xs={12}>
							<Controller
								control={control}
								name="contacts.email"
								render={({ field }) => (
									<Input
										{...field}
										id="email-input"
										label={t('Email')}
										type="email"
										disabled={false}
										error={errors.contacts?.email?.message}
									/>
								)}
							/>
						</Col>
						<Col xs={12}>
							<React.Fragment>
								<label
									className="label-text"
									htmlFor="cellPhone">
									{t('Phone')}
								</label>
								<Controller
									control={control}
									name="contacts.cellPhone"
									render={({ field }) => (
										<ReactPhoneInput
											{...field}
											inputProps={{
												name: 'contacts.cellPhone',
												id: 'cellPhone',
											}}
											containerClass={`react-tel-input ${
												errors.contacts?.cellPhone
													? 'is-invalid'
													: ''
											}`}
											onlyCountries={
												appConfig.country === 'no'
													? [] // Show us all countries if the market is Norway
													: allowMultipleFormats
														? [
																'fi',
																'ee',
																'lt',
																'lv',
															]
														: [appConfig.country]
											}
											country={
												!['fi', 'ee', 'lt'].includes(
													appConfig.country,
												)
													? appConfig.country
													: undefined
											}
											value={
												user.contacts?.cellPhone || ''
											}
											onChange={(value: string) => {
												field.onChange(
													value.startsWith('+')
														? value
														: '+' + value,
												)
											}}
											isValid={
												!errors.contacts?.cellPhone
											}
											disableDropdown={[
												'lt',
												'lv',
											].includes(appConfig.country)}
											countryCodeEditable={
												![
													'fi',
													'lt',
													'lv',
													'se',
													'no',
													'dk',
												].includes(appConfig.country)
											}
										/>
									)}
								/>
								{errors.contacts?.cellPhone && (
									<Form.Control.Feedback
										type="invalid"
										style={{ display: 'block' }}>
										{t('InvalidPhone')}
									</Form.Control.Feedback>
								)}
							</React.Fragment>
							<Form.Control.Feedback type="invalid">
								{t('InvalidPhone')}
							</Form.Control.Feedback>
						</Col>
					</Row>
					<div className="outer-buttons">					
						<button
                            type="button"
                            className="btn btn-primary d-inline address-btn float-right mt-3 ml-2"
                            disabled={isSubmitting}
                            onClick={(e) => {
                                e.preventDefault()
                                setTimeout(() => {
                                    handleSubmit(onSubmit)()
                                }, 400)
                            }}>
                            {t('Save')}
                        </button>          

						<div className="d-inline float-right mt-3 pointer">
							<button
                                type="button"
                                className="address-btn-back btn-border btn"
                                onClick={(e) => {
                                    e.preventDefault()
                                    setTimeout(() => {
                                        hide()
                                    }, 400)
                                }}>
                                <FontAwesomeIcon
                                    className="mr-2 mt-1"
                                    icon="angle-left"
                                />
                                {t('Back')}
                            </button>

						</div>
					</div>
				</Form>
			</div>
		</div>
	)
}
export default ChangeAddress